import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, Mic, Image, Smile, ChevronLeft, Bot, Trash2, UserPlus, MoreVertical
} from 'lucide-react';
import api from '../api';
import MessageContextMenu from './chat/MessageContextMenu';
import { NoAutoFillInput } from './ui/NoAutoFillInput';

const PrivateChat = ({ 
  friend, 
  userId, 
  userName, 
  userAvatar, 
  onClose, 
  onDeleteFriend,
  onCreateGroup 
}) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [aiAutoReply, setAiAutoReply] = useState(false);
  const [contextMenu, setContextMenu] = useState({ isOpen: false, position: { x: 0, y: 0 }, msg: null });
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chatId = [userId, friend.friendId].sort().join('_');
  
  const friendDisplayName = friend.friendName || friend.friendEmail?.split('@')[0] || friend.friendId;

  useEffect(() => {
    if (friend.aiAutoReply !== undefined) {
      setAiAutoReply(friend.aiAutoReply === 1);
    }
  }, [friend.aiAutoReply]);

  const handleToggleAutoReply = async () => {
    const newValue = !aiAutoReply;
    setAiAutoReply(newValue);
    try {
      await api.setAutoReply(userId, friend.friendId, newValue);
    } catch (err) {
      console.error('设置自动回复失败:', err);
      setAiAutoReply(!newValue);
    }
  };

  const emojis = ['😀', '😂', '🥰', '😎', '🤔', '👍', '❤️', '🔥', '🎉', '👏', '🤖', '👻', '👽', '🤡', '💀', '👾'];

  const loadMessages = useCallback(async () => {
    try {
      setIsLoading(true);
      const msgs = await api.getPrivateMessages(chatId, 100);
      setMessages(msgs);
    } catch (err) {
      console.error('加载消息失败:', err);
    } finally {
      setIsLoading(false);
    }
  }, [chatId]);

  useEffect(() => {
    loadMessages();
    
    const setupSocket = () => {
      if (!api.socket) {
        api.initSocket();
      }
      
      const doSetup = () => {
        console.log('[PrivateChat] Socket connected, joining chat room:', chatId);
        api.socket.emit('join-chat', chatId);
      };
      
      if (api.socket.connected) {
        doSetup();
      } else {
        api.socket.once('connect', doSetup);
      }
      
      const handlePrivateMessage = (data) => {
        console.log('[PrivateChat] Received private_message:', data);
        if (data.chatId === chatId) {
          setMessages(prev => {
            const exists = prev.some(m => m.id === data.id);
            if (exists) return prev;
            return [...prev, data];
          });
        }
      };
      
      api.socket.on('private_message', handlePrivateMessage);
      
      return () => {
        if (api.socket) {
          api.socket.off('private_message', handlePrivateMessage);
        }
      };
    };
    
    const cleanup = setupSocket();
    
    return () => {
      if (cleanup) cleanup();
    };
  }, [chatId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    
    const content = inputValue.trim();
    try {
      const tempMsg = {
        id: 'temp_' + Date.now(),
        chatId,
        senderId: userId,
        senderName: userName,
        senderAvatar: userAvatar || '',
        content,
        type: 'text',
        createdAt: new Date().toISOString()
      };
      setMessages(prev => [...prev, tempMsg]);
      setInputValue('');
      
      await api.sendPrivateMessage(
        chatId,
        userId,
        userName,
        userAvatar || '',
        content,
        'text'
      );
    } catch (err) {
      console.error('发送消息失败:', err);
      alert('发送失败: ' + err.message);
    }
  };

  const handleEmojiSelect = (emoji) => {
    setInputValue(prev => prev + emoji);
    setShowEmoji(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const msg = await api.sendPrivateMessage(
          chatId,
          userId,
          userName,
          userAvatar || '',
          '图片',
          'image',
          reader.result
        );
        setMessages(prev => [...prev, msg]);
      } catch (err) {
        console.error('发送图片失败:', err);
        alert('发送图片失败: ' + err.message);
      }
    };
    reader.readAsDataURL(file);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const chunks = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      
      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            const msg = await api.sendPrivateMessage(
              chatId,
              userId,
              userName,
              userAvatar || '',
              '语音',
              'voice',
              reader.result
            );
            setMessages(prev => [...prev, msg]);
          } catch (err) {
            console.error('发送语音失败:', err);
          }
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('录音失败:', err);
      alert('无法访问麦克风');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleLongPress = (e, msg) => {
    e.preventDefault();
    const touch = e.touches ? e.touches[0] : e;
    setContextMenu({
      isOpen: true,
      position: { x: touch.clientX, y: touch.clientY },
      msg
    });
  };

  const handleCopy = () => {
    if (contextMenu.msg) {
      navigator.clipboard.writeText(contextMenu.msg.content);
      alert('已复制');
    }
  };

  const handleDelete = async () => {
    if (!contextMenu.msg) return;
    try {
      await api.deletePrivateMessage(chatId, contextMenu.msg.id, userId);
      setMessages(prev => prev.filter(m => m.id !== contextMenu.msg.id));
    } catch (err) {
      alert('删除失败: ' + err.message);
    }
  };

  const handleRecall = async () => {
    if (!contextMenu.msg) return;
    try {
      await api.recallPrivateMessage(chatId, contextMenu.msg.id, userId);
      setMessages(prev => prev.map(m => 
        m.id === contextMenu.msg.id ? { ...m, recalled: 1, content: '消息已撤回' } : m
      ));
    } catch (err) {
      alert('撤回失败: ' + err.message);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#050510] flex flex-col">
      <div className="h-28 flex items-end justify-between px-6 pb-4 glass-card rounded-b-[40px] z-20">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10">
            <ChevronLeft size={24} className="text-white" />
          </button>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-white">{friendDisplayName}</h2>
            <p className="text-xs text-gray-400">{aiAutoReply ? 'AI分身自动回复已开启' : '在线'}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleAutoReply}
            className={`p-2 rounded-full transition-colors ${
              aiAutoReply 
                ? 'bg-[var(--neon-primary)] text-black' 
                : 'bg-white/10 text-white'
            }`}
            title="AI分身自动回复"
          >
            <Bot size={20} />
          </button>
          
          <div className="relative">
            <button
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className="p-2 rounded-full hover:bg-white/10"
            >
              <MoreVertical size={20} className="text-white" />
            </button>
            
            {showMoreMenu && (
              <div className="absolute right-0 top-12 bg-[#1a1a2e] rounded-xl border border-white/10 py-2 min-w-[140px] shadow-xl z-50">
                <button
                  onClick={() => {
                    setShowMoreMenu(false);
                    onCreateGroup && onCreateGroup();
                  }}
                  className="w-full px-4 py-2 text-left text-white text-sm hover:bg-white/10 flex items-center gap-2"
                >
                  <UserPlus size={16} />
                  发起群聊
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('确定要删除该好友吗？')) {
                      onDeleteFriend(friend.friendId);
                    }
                    setShowMoreMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-red-400 text-sm hover:bg-white/10 flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  删除好友
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showMoreMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowMoreMenu(false)}
        />
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-40">
        {isLoading && messages.length === 0 && (
          <div className="text-center text-gray-500 py-10">
            <p>加载中...</p>
          </div>
        )}
        
        {messages.length === 0 && !isLoading && (
          <div className="text-center text-gray-500 py-10">
            <p>还没有消息，打个招呼吧！</p>
          </div>
        )}
        
        {messages.map((msg) => {
          const isMe = msg.senderId === userId;
          const isRecalled = msg.recalled || msg.content === '消息已撤回';
          
          return (
            <div 
              key={msg.id} 
              className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
              onContextMenu={(e) => !isRecalled && handleLongPress(e, msg)}
              onTouchStart={(e) => {
                if (isRecalled) return;
                const timer = setTimeout(() => handleLongPress(e, msg), 500);
                e.target.addEventListener('touchend', () => clearTimeout(timer), { once: true });
              }}
            >
              <div className={`max-w-[75%] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
                {!isMe && (
                  <span className="text-[10px] text-gray-500 mb-1 ml-1">{msg.senderName}</span>
                )}
                
                {isRecalled ? (
                  <div className="text-xs text-gray-500 italic px-4 py-2">
                    消息已撤回
                  </div>
                ) : (
                  <div className={`rounded-2xl px-4 py-3 ${
                    isMe 
                      ? 'bg-[var(--neon-primary)] text-black rounded-br-none' 
                      : 'glass-card text-white rounded-bl-none'
                  }`}>
                    {msg.type === 'text' && <p className="text-sm">{msg.content}</p>}
                    {msg.type === 'image' && (
                      <img src={msg.mediaUrl} alt="图片" className="max-w-full rounded-lg" />
                    )}
                    {msg.type === 'voice' && (
                      <div className="flex items-center gap-2">
                        <span>🎤 语音消息</span>
                      </div>
                    )}
                  </div>
                )}
                
                <span className={`text-[10px] text-gray-500 mt-1 ${isMe ? 'mr-1' : 'ml-1'}`}>
                  {formatTime(msg.createdAt)}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {showEmoji && (
        <div className="fixed bottom-24 left-4 right-4 bg-[#1a1a2e] rounded-2xl p-4 z-50 border border-white/10">
          <div className="grid grid-cols-8 gap-2">
            {emojis.map(emoji => (
              <button
                key={emoji}
                onClick={() => handleEmojiSelect(emoji)}
                className="text-2xl p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      <MessageContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
        onCopy={handleCopy}
        onDelete={handleDelete}
        onRecall={handleRecall}
        canRecall={contextMenu.msg?.senderId === userId && 
          (new Date() - new Date(contextMenu.msg?.createdAt)) / (1000 * 60) <= 2}
        isRecalled={contextMenu.msg?.recalled}
      />

      <div className="fixed bottom-0 left-0 w-full px-4 pb-6 pt-4 bg-gradient-to-t from-[#050510] via-[#050510]/95 to-transparent safe-bottom">
        <div className="glass-card rounded-[32px] p-2 flex items-center gap-2">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            ref={fileInputRef}
            onChange={handleImageUpload}
          />
          
          <button 
            onClick={() => fileInputRef.current?.click()}
            className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-[var(--neon-primary)] hover:bg-white/10"
          >
            <Image size={20} />
          </button>
          
          <button 
            onClick={() => setShowEmoji(!showEmoji)}
            className={`w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 ${showEmoji ? 'text-[var(--neon-primary)]' : 'text-gray-400'}`}
          >
            <Smile size={20} />
          </button>
          
          <NoAutoFillInput
            type="text"
            inputMode="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="输入消息..."
            name="private-message"
            id="private-message"
            className="flex-1 bg-transparent border-none outline-none text-white py-2"
          />
          
          <button 
            onMouseDown={startRecording}
            onMouseUp={stopRecording}
            onTouchStart={startRecording}
            onTouchEnd={stopRecording}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              isRecording ? 'bg-red-500 text-white animate-pulse' : 'text-gray-400 hover:text-[var(--neon-primary)] hover:bg-white/10'
            }`}
          >
            <Mic size={20} />
          </button>
          
          <button
            onClick={handleSend}
            disabled={!inputValue.trim()}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              inputValue.trim() 
                ? 'bg-[var(--neon-primary)] text-black' 
                : 'bg-white/10 text-gray-500'
            }`}
          >
            <Send size={18} />
          </button>
        </div>
        
        {isRecording && (
          <div className="text-center mt-2 text-red-400 text-sm animate-pulse">
            正在录音...
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivateChat;
